// ControlSection.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, ArrowsPointingOutIcon, DocumentDuplicateIcon, PencilIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import QuestionTable from './QuestionTable';
import { useAIAnalysis } from './AIAnalysisHelper';
import { methodologyService } from '../services/methodologyService';
import { useAuth } from '../contexts/AuthContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import MarkdownRenderer, { SimpleTextRenderer } from './MarkdownRenderer';
import TaskCreateModal from './Tasks/TaskCreateModal';
import EvidenceTab from './EvidenceTab';

const EVIDENCE_TAB_CONTROL_ID = 'ML1-MFA-01';

/**
 * ControlSection Component
 * Displays controls with a sliding panel interface inspired by ISO27001
 * 
 * @param {Object} props
 * @param {string} props.controlName - Name of the current control
 * @param {Array} props.questions - Array of questions for the control
 * @param {Object} props.answers - Object containing answers data
 * @param {Function} props.onAnswerChange - Callback for answer changes
 * @param {string} props.summaryRecommendations - Summary recommendations text
 * @param {boolean} props.hideOverallProgress - Whether to hide the overall progress section
 * @param {Function} props.getQuestionsForML - Function to get questions for a specific maturity level
 * @param {string} props.maturityLevel - Current maturity level of the control
 * @param {string} props.activeTab - Current active tab
 * @param {Function} props.getControlCompliance - Function to get control compliance
 * @param {Function} props.getControlDescription - Function to get control description
 * @param {Function} props.handleFindingsChange - Function to handle findings change
 */
const ControlSection = ({
  controlName,
  questions,
  answers,
  onAnswerChange,
  summaryRecommendations,
  hideOverallProgress = true,
  getQuestionsForML,
  maturityLevel,
  activeTab,
  getControlCompliance,
  getControlDescription,
  handleFindingsChange
}) => {
  // Get AI analysis functionality from context
  const { isConnected, analyzeEvidence } = useAIAnalysis();
  
  // Get current user for methodology customization
  const { user } = useAuth();
  
  // Permission check for AI-Analysis feature
  const { hasModuleAccess } = useModuleAccess();
  const hasAIAnalysisAccess = hasModuleAccess('AI-Analysis');
  
  // State for tracking analysis status
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // State for task creation modal
  const [isTaskCreateModalOpen, setIsTaskCreateModalOpen] = useState(false);
  const [taskCreateInitialValues, setTaskCreateInitialValues] = useState({});
  
  // Tab system for modal
  const [activeModalTab, setActiveModalTab] = useState('overview');

  // State for methodology data
  const [methodologies, setMethodologies] = useState(new Map());
  const [loadingMethodologies, setLoadingMethodologies] = useState(new Set());
  
  // Ref to track what's currently being loaded to prevent duplicates
  const loadingRef = useRef(new Set());

  /**
   * Load methodology for a specific control ID
   * @param {string} controlId - The control ID to load methodology for
   */
  const loadMethodology = useCallback(async (controlId) => {
    // Prevent duplicate calls using ref
    if (loadingRef.current.has(controlId)) {
      return;
    }
    
    loadingRef.current.add(controlId);
    setLoadingMethodologies(prev => new Set(prev).add(controlId));

    try {
      const methodologyData = await methodologyService.getMethodology(
        controlId, 
        user?.id
      );
      
      setMethodologies(prev => new Map(prev).set(controlId, methodologyData));
    } catch (error) {
      console.error(`Error loading methodology for ${controlId}:`, error);
      // Set fallback methodology
      setMethodologies(prev => new Map(prev).set(controlId, {
        methodology: 'No methodology available',
        is_custom: false,
        is_fallback: true
      }));
    } finally {
      loadingRef.current.delete(controlId);
      setLoadingMethodologies(prev => {
        const newSet = new Set(prev);
        newSet.delete(controlId);
        return newSet;
      });
    }
  }, [user?.id]);

  /**
   * Get methodology text for a control ID
   * @param {string} controlId - The control ID
   * @returns {string} The methodology text
   */
  const getMethodologyText = (controlId) => {
    const methodologyData = methodologies.get(controlId);
    if (methodologyData) {
      return methodologyData.methodology;
    }
    return 'Loading methodology...';
  };

  // Group questions by maturity level
  const groupedQuestions = {
    ML1: questions.filter(q => q.id.startsWith('ML1')),
    ML2: questions.filter(q => q.id.startsWith('ML2')),
    ML3: questions.filter(q => q.id.startsWith('ML3'))
  };
  
  // Active maturity level tab
  const [activeMLTab, setActiveMLTab] = useState(
    maturityLevel || 
    (groupedQuestions.ML1.length > 0 ? 'ML1' : 
    groupedQuestions.ML2.length > 0 ? 'ML2' : 
    'ML3')
  );
  
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const isEvidenceTabEnabled = selectedQuestion?.id === EVIDENCE_TAB_CONTROL_ID;

  // Load methodologies when questions or user changes
  useEffect(() => {
    const loadMethodologies = async () => {
      const controlIds = questions.map(q => q.id);
      
      // Load methodologies for all questions in this control
      for (const controlId of controlIds) {
        loadMethodology(controlId);
      }
    };

    if (questions.length > 0) {
      loadMethodologies();
    }
  }, [questions, user?.id, loadMethodology]);

  // Load methodology for selected question if not already loaded
  useEffect(() => {
    if (selectedQuestion?.id) {
      loadMethodology(selectedQuestion.id);
    }
  }, [selectedQuestion?.id, user?.id, loadMethodology]);
  
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Trigger server auto-save when switching tabs.
  useEffect(() => {
    // Trigger auto-save when switching tabs (if we have a selected question)
    if (selectedQuestion && handleFindingsChange) {
      handleFindingsChange();
    }
  }, [activeModalTab, selectedQuestion, handleFindingsChange]);

  useEffect(() => {
    if (!isEvidenceTabEnabled && activeModalTab === 'evidence') {
      setActiveModalTab('overview');
    }
  }, [activeModalTab, isEvidenceTabEnabled]);

  // Keyboard navigation for tabs
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle keyboard shortcuts when modal is open
      if (!isPanelOpen) return;
      
      if (e.ctrlKey || e.metaKey) {
        switch(e.key) {
          case '1':
            e.preventDefault();
            setActiveModalTab('overview');
            break;
          case '2':
            e.preventDefault();
            setActiveModalTab('assessment');
            break;
          case '3':
            e.preventDefault();
            setActiveModalTab('recommendations');
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isPanelOpen]);

  // Update activeMLTab when maturityLevel changes
  useEffect(() => {
    if (maturityLevel) {
      setActiveMLTab(maturityLevel);
    }
  }, [maturityLevel]);

  // Get active questions based on maturity level tab
  const getActiveQuestions = () => {
    return groupedQuestions[activeMLTab] || [];
  };

  // Calculate completion status for each maturity level
  const getCompletionStatus = (ml) => {
    const mlQuestions = groupedQuestions[ml] || [];
    if (mlQuestions.length === 0) return { completed: 0, total: 0, percentage: 0 };
    
    const completed = mlQuestions.filter(q => 
      answers[q.id] && answers[q.id].value && answers[q.id].value !== ''
    ).length;
    
    return {
      completed,
      total: mlQuestions.length,
      percentage: mlQuestions.length > 0 ? (completed / mlQuestions.length) * 100 : 0
    };
  };

  // Open panel with selected question
  const openPanel = (question) => {
    // First set the question to trigger render
    setSelectedQuestion(question);
    
    // Then set panel open state after a short delay to ensure smooth animation
    // This allows the panel to first render off-screen, then animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsPanelOpen(true);
      });
    });
  };

  // Close panel
  const closePanel = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedQuestion(null), 500); // Increased from 300ms to match new animation duration
  };

  // Get completion status for individual tabs
  const getTabCompletionStatus = (questionId, tabType) => {
    if (!answers[questionId]) return { completed: false, hasContent: false };
    
    const answer = answers[questionId];
    
    switch (tabType) {
      case 'overview':
        return { 
          completed: !!answer.value && answer.value !== 'not_assessed',
          hasContent: !!answer.value
        };
      case 'assessment':
        return { 
          completed: !!(answer.findings && answer.justification),
          hasContent: !!(answer.findings || answer.justification || answer.aiAnalysis)
        };
      case 'recommendations':
        return { 
          completed: !!answer.recommendations,
          hasContent: !!answer.recommendations
        };
      default:
        return { completed: false, hasContent: false };
    }
  };

  // Get assessment options with color coding that matches status descriptions
  const getOptions = () => {
    return [
      { value: 'not_assessed', label: 'Not Assessed', color: 'gray' },
      { value: 'effective', label: 'Effective', color: 'green' },
      { value: 'alternate_control', label: 'Alternate Control', color: 'indigo' },
      { value: 'ineffective', label: 'Ineffective', color: 'red' },
      { value: 'no_visibility', label: 'No Visibility', color: 'yellow' },
      { value: 'not_implemented', label: 'Not Implemented', color: 'orange' },
      { value: 'not_applicable', label: 'Not Applicable', color: 'purple' }
    ];
  };

  // Generate AI analysis by sending findings to the AI bridge
  const generateAIAnalysis = async (questionId) => {
    console.log(`Generating AI analysis for ${questionId}`);
    
    // Make sure we have test findings to analyze
    const findings = answers[questionId]?.findings;
    if (!findings || findings.trim() === '') {
      // Update the answer with an error message if no findings provided
      onAnswerChange(
        questionId, 
        answers[questionId]?.value || '', 
        'aiAnalysis', 
        'Please provide test findings to analyze.'
      );
      return;
    }
    
    // Set analyzing state to show loading
    setIsAnalyzing(true);
    
    try {
      // Extract maturity level from question ID
      const mlMatch = questionId.match(/^(ML[123])/);
      const mlString = mlMatch ? mlMatch[1] : 'ML1';
      
      // Get test methodology for this control
      const methodology = getMethodologyText(questionId);
      
      // Prepare context for analysis
      const context = {
        controlId: questionId,
        evidence: findings,
        maturityLevel: mlString,
        controlRequirement: selectedQuestion.question,
        methodology: methodology
      };
      
      // Call AI analysis service
      const result = await analyzeEvidence(context);
      
      if (result.success) {
        // Update the answer with the analysis result
        onAnswerChange(
          questionId, 
          answers[questionId]?.value || '', 
          'aiAnalysis', 
          result.rawAnalysis || 'Analysis completed, but no specific recommendations were provided.'
        );
      } else {
        // Handle error
        console.error('AI Analysis failed:', result.error);
        onAnswerChange(
          questionId, 
          answers[questionId]?.value || '', 
          'aiAnalysis', 
          `Analysis failed: ${result.error || 'Unknown error'}. Please try again later.`
        );
      }
    } catch (error) {
      console.error('Error in generateAIAnalysis:', error);
      onAnswerChange(
        questionId, 
        answers[questionId]?.value || '', 
        'aiAnalysis', 
        `Error: ${error.message}. Please try again later.`
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Helper function to open task creation modal with pre-filled data
  const openTaskModal = (taskType, title, description) => {
    // Set initial values for the task creation modal
    setTaskCreateInitialValues({
      control_id: selectedQuestion?.id || '',
      task_type: taskType,
      title: title,
      description: description
    });
    setIsTaskCreateModalOpen(true);
  };

  // Helper function to handle task creation completion
  const handleTaskCreated = (newTask) => {
    // Task successfully created - could refresh task list here
    console.log('Task created:', newTask);
    setIsTaskCreateModalOpen(false);
  };

  // Panel render function for portal
  const renderPanel = () => {
    if (!selectedQuestion) return null;
    
    return ReactDOM.createPortal(
      <div 
        className={`fixed inset-0 z-[9999] overflow-hidden transition-opacity duration-500 ease-in-out ${
          isPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        aria-labelledby="slide-over-title" 
        role="dialog" 
        aria-modal="true"
      >
        {/* Background overlay with fade transition */}
        <div 
          className={`absolute inset-0 bg-gray-500 transition-opacity duration-500 ease-in-out ${
            isPanelOpen ? 'opacity-75' : 'opacity-0'
          }`}
          onClick={closePanel}
        ></div>

        {/* Panel container */}
        <div className="fixed inset-y-0 right-0 flex max-w-full pointer-events-auto">
          <div 
            className={`relative w-screen max-w-6xl transform transition-transform duration-500 ease-in-out ${
              isPanelOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Panel */}
            <div className="h-full flex flex-col bg-white shadow-xl overflow-hidden">
              {/* Fixed Header - Always visible */}
              <div className="flex-shrink-0 bg-blue-900 text-white px-6 py-5 flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-bold tracking-tight" id="slide-over-title">
                    {selectedQuestion.id}
                  </h2>
                </div>
                <div className="flex items-center space-x-3 ml-3">
                  <button
                    className="p-1.5 text-white hover:text-gray-200 focus:outline-none rounded-full hover:bg-blue-800 transition-colors duration-200"
                    onClick={closePanel}
                    aria-label="Close drawer"
                  >
                    <XMarkIcon className="h-7 w-7" />
                  </button>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 bg-white px-6">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveModalTab('overview')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeModalTab === 'overview'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2">
                        <span>Overview</span>
                        {selectedQuestion && getTabCompletionStatus(selectedQuestion.id, 'overview').completed && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                        {selectedQuestion && !getTabCompletionStatus(selectedQuestion.id, 'overview').completed && getTabCompletionStatus(selectedQuestion.id, 'overview').hasContent && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">Control & methodology</span>
                    </div>
                  </button>
                  {isEvidenceTabEnabled && (
                    <button
                      onClick={() => setActiveModalTab('evidence')}
                      aria-controls="evidence-tab-panel"
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-600 ${
                        activeModalTab === 'evidence'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span>Evidence</span>
                        <span className="text-xs text-gray-400 mt-1">Evidence workspace</span>
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => setActiveModalTab('assessment')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeModalTab === 'assessment'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2">
                        <span>Assessment</span>
                        {selectedQuestion && getTabCompletionStatus(selectedQuestion.id, 'assessment').completed && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                        {selectedQuestion && !getTabCompletionStatus(selectedQuestion.id, 'assessment').completed && getTabCompletionStatus(selectedQuestion.id, 'assessment').hasContent && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">Testing & analysis</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveModalTab('recommendations')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeModalTab === 'recommendations'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2">
                        <span>Recommendations</span>
                        {selectedQuestion && getTabCompletionStatus(selectedQuestion.id, 'recommendations').completed && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                        {selectedQuestion && !getTabCompletionStatus(selectedQuestion.id, 'recommendations').completed && getTabCompletionStatus(selectedQuestion.id, 'recommendations').hasContent && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">Improvement actions</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveModalTab('tasks')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeModalTab === 'tasks'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2">
                        <span>Tasks</span>
                      </div>
                      <span className="text-xs text-gray-400 mt-1">Control-specific tasks</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Scrollable Content Area (everything below tab navigation) */}
              <div className="flex-1 overflow-y-auto">
                {/* Tab Content */}
                {activeModalTab === 'overview' && (
                  <div className="bg-white p-6 animate-in fade-in duration-300">
                  <div className="space-y-6">
                    {/* Control Name/Title */}
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedQuestion.question}
                      </h2>
                    </div>
                  
                    {/* Control Status Buttons */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Control Status</h3>
                      <div className="flex flex-wrap gap-2">
                        {getOptions().map(option => (
                          <button
                            key={option.value}
                            className={`px-4 py-2 text-sm rounded-md font-medium border ${
                              answers[selectedQuestion.id]?.value === option.value
                                ? option.color === 'green' ? 'bg-green-100 text-green-800 border-green-300' :
                                  option.color === 'indigo' ? 'bg-indigo-100 text-indigo-800 border-indigo-300' :
                                  option.color === 'red' ? 'bg-red-100 text-red-800 border-red-300' :
                                  option.color === 'orange' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                  option.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                  option.color === 'purple' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                  'bg-gray-100 text-gray-700 border-gray-300'
                                : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => onAnswerChange(selectedQuestion.id, option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Test Methodology */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-900">Test Methodology</h3>
                        {methodologies.get(selectedQuestion.id)?.is_custom && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Customized
                          </span>
                        )}
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        {loadingMethodologies.has(selectedQuestion.id) ? (
                          <div className="flex items-center text-gray-500">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading methodology...
                          </div>
                        ) : (
                          <MarkdownRenderer 
                            content={getMethodologyText(selectedQuestion.id)}
                            className="text-gray-700"
                          />
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                )}

                {activeModalTab === 'evidence' && isEvidenceTabEnabled && <EvidenceTab />}

                {activeModalTab === 'assessment' && (
                  <div className="p-6 space-y-8 animate-in fade-in duration-300">
                    {/* Quick Methodology Reference - Collapsible */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <details>
                        <summary className="text-sm font-medium text-blue-900 cursor-pointer hover:text-blue-700">
                          📋 Quick Methodology Reference
                        </summary>
                        <div className="mt-3 text-sm text-blue-800">
                          <MarkdownRenderer 
                            content={getMethodologyText(selectedQuestion.id)}
                            className="text-blue-800"
                          />
                        </div>
                      </details>
                    </div>
                  {/* Test Findings */}
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">Test Findings</h3>
                    <textarea
                      value={answers[selectedQuestion.id]?.findings || ''}
                      onChange={(e) => onAnswerChange(selectedQuestion.id, answers[selectedQuestion.id]?.value || '', 'findings', e.target.value)}
                      className="w-full p-4 border rounded-lg text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Document your test findings here..."
                      rows="6"
                    />
                  </div>

                  {/* AI Analysis - Conditional Rendering */}
                  {hasAIAnalysisAccess && (
                    <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-medium text-gray-900">AI-Assisted Analysis</h3>
                      <button 
                        className={`px-3 py-1 ${isConnected ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'} text-white text-sm rounded transition-colors flex items-center`}
                        onClick={() => generateAIAnalysis(selectedQuestion.id)}
                        disabled={isAnalyzing || !isConnected}
                      >
                        {isAnalyzing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                          </>
                        ) : (
                          'Generate'
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500 italic min-h-[120px]">
                      {answers[selectedQuestion.id]?.aiAnalysis ? (
                        <div className="text-gray-700 whitespace-pre-wrap">{answers[selectedQuestion.id].aiAnalysis}</div>
                      ) : isAnalyzing ? (
                        <div className="flex items-center justify-center h-full">
                          <svg className="animate-spin h-8 w-8 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      ) : !isConnected ? (
                        <p className="text-yellow-600">AI connection unavailable. Please try again later.</p>
                      ) : (
                        <p>AI analysis will appear here after clicking Generate</p>
                      )}
                    </div>
                    </div>
                  )}
                  
                    {/* Assessment Justification */}
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 mb-3">Assessment Justification</h3>
                      <textarea
                        value={answers[selectedQuestion.id]?.justification || ''}
                        onChange={(e) => onAnswerChange(selectedQuestion.id, answers[selectedQuestion.id]?.value || '', 'justification', e.target.value)}
                        className="w-full p-4 border rounded-lg text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Document your justification for the assessment..."
                        rows="6"
                      />
                    </div>
                  </div>
                )}

                {activeModalTab === 'recommendations' && (
                  <div className="p-6 space-y-8 animate-in fade-in duration-300">
                    {/* Recommendations */}
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 mb-3">Recommendations</h3>
                      <textarea
                        value={answers[selectedQuestion.id]?.recommendations || ''}
                        onChange={(e) => onAnswerChange(selectedQuestion.id, answers[selectedQuestion.id]?.value || '', 'recommendations', e.target.value)}
                        className="w-full p-4 border rounded-lg text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Provide recommendations for improvement..."
                        rows="8"
                      />
                    </div>

                    {/* Summary Insights */}
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 mb-3">Summary Insights</h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-blue-900">Current Status:</span>
                            <span className={`px-2 py-1 rounded text-sm ${
                              answers[selectedQuestion.id]?.value === 'effective' ? 'bg-green-100 text-green-800' :
                              answers[selectedQuestion.id]?.value === 'alternate_control' ? 'bg-indigo-100 text-indigo-800' :
                              answers[selectedQuestion.id]?.value === 'ineffective' ? 'bg-red-100 text-red-800' :
                              answers[selectedQuestion.id]?.value === 'not_implemented' ? 'bg-orange-100 text-orange-800' :
                              answers[selectedQuestion.id]?.value === 'no_visibility' ? 'bg-yellow-100 text-yellow-800' :
                              answers[selectedQuestion.id]?.value === 'not_applicable' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {answers[selectedQuestion.id]?.value ? 
                                getOptions().find(opt => opt.value === answers[selectedQuestion.id]?.value)?.label || 'Not Set'
                                : 'Not Set'
                              }
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-blue-900">Findings:</span>
                              <p className="text-blue-800 mt-1">
                                {answers[selectedQuestion.id]?.findings ? 
                                  `${answers[selectedQuestion.id].findings.substring(0, 100)}${answers[selectedQuestion.id].findings.length > 100 ? '...' : ''}`
                                  : 'Not documented'
                                }
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">Justification:</span>
                              <p className="text-blue-800 mt-1">
                                {answers[selectedQuestion.id]?.justification ? 
                                  `${answers[selectedQuestion.id].justification.substring(0, 100)}${answers[selectedQuestion.id].justification.length > 100 ? '...' : ''}`
                                  : 'Not provided'
                                }
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-blue-900">AI Analysis:</span>
                              <p className="text-blue-800 mt-1">
                                {answers[selectedQuestion.id]?.aiAnalysis ? 'Available' : 'Not generated'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeModalTab === 'tasks' && (
                  <div className="p-6 space-y-6 animate-in fade-in duration-300">
                    {/* Tasks for this control */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-medium text-gray-900">Tasks for {selectedQuestion.id}</h3>
                        <button
                          onClick={() => openTaskModal('general', `Task for ${selectedQuestion.id}`, `General task related to ${selectedQuestion.id}`)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Create Task
                        </button>
                      </div>
                      
                      {/* Task List */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="text-center text-gray-500 py-8">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No tasks yet</h4>
                          <p className="text-gray-600">
                            Create tasks to track work related to this control. Tasks can include remediation activities, 
                            evidence collection, testing procedures, or any other activities needed for compliance.
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            Tasks will be fully functional once the E8 Tasks module database is set up.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Quick Actions</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          onClick={() => openTaskModal('remediation', 'Remediation Task', `Address identified issues for ${selectedQuestion.id}`)}
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">Create Remediation Task</div>
                            <div className="text-sm text-gray-500">Address identified issues</div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => openTaskModal('assessment', 'Assessment Task', `Schedule regular testing for ${selectedQuestion.id}`)}
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">Create Assessment Task</div>
                            <div className="text-sm text-gray-500">Schedule regular testing</div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => openTaskModal('documentation', 'Documentation Task', `Update procedures or evidence for ${selectedQuestion.id}`)}
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">Create Documentation Task</div>
                            <div className="text-sm text-gray-500">Update procedures or evidence</div>
                          </div>
                        </button>
                        
                        <button
                          onClick={() => openTaskModal('review', 'Review Task', `Periodic compliance review for ${selectedQuestion.id}`)}
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">Create Review Task</div>
                            <div className="text-sm text-gray-500">Periodic compliance review</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="flex flex-col">
      {/* Maturity Level Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex">
          {Object.keys(groupedQuestions).map(ml => (
            groupedQuestions[ml].length > 0 && (
              <button
                key={ml}
                onClick={() => setActiveMLTab(ml)}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeMLTab === ml
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <span>{ml}</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                    getCompletionStatus(ml).completed === getCompletionStatus(ml).total
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {getCompletionStatus(ml).completed}/{getCompletionStatus(ml).total}
                  </span>
                </div>
              </button>
            )
          ))}
        </div>
      </div>

      {/* Controls List */}
      <div className="space-y-3 pb-20">
        {getActiveQuestions().map(question => (
          <div 
            key={question.id}
            className={`relative border border-gray-200 rounded-lg shadow-sm overflow-hidden ${
              answers[question.id]?.value === 'effective' ? 'bg-green-50' :
              answers[question.id]?.value === 'alternate_control' ? 'bg-indigo-50' :
              answers[question.id]?.value === 'ineffective' ? 'bg-red-50' :
              answers[question.id]?.value === 'not_implemented' ? 'bg-orange-50' :
              answers[question.id]?.value === 'not_applicable' ? 'bg-purple-50' :
              answers[question.id]?.value === 'no_visibility' ? 'bg-yellow-50' :
              answers[question.id]?.value === 'not_assessed' ? 'bg-gray-50' : 'bg-white'
            }`}
          >
            <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => openPanel(question)}>
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md text-xs font-medium">
                    {question.id}
                  </span>
                  {answers[question.id]?.value && (
                    <span className={`ml-2 px-2 py-0.5 rounded-md text-xs font-medium
                      ${answers[question.id]?.value === 'effective' ? 'bg-green-100 text-green-800' :
                        answers[question.id]?.value === 'alternate_control' ? 'bg-indigo-100 text-indigo-800' :
                        answers[question.id]?.value === 'ineffective' ? 'bg-red-100 text-red-800' :
                        answers[question.id]?.value === 'not_implemented' ? 'bg-orange-100 text-orange-800' :
                        answers[question.id]?.value === 'no_visibility' ? 'bg-yellow-100 text-yellow-800' :
                        answers[question.id]?.value === 'not_assessed' ? 'bg-gray-100 text-gray-700' :
                        answers[question.id]?.value === 'not_applicable' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-700'}`}
                    >
                      {answers[question.id]?.value === 'effective' ? 'Effective' :
                        answers[question.id]?.value === 'alternate_control' ? 'Alternate Control' :
                        answers[question.id]?.value === 'ineffective' ? 'Ineffective' :
                        answers[question.id]?.value === 'not_implemented' ? 'Not Implemented' :
                        answers[question.id]?.value === 'no_visibility' ? 'No Visibility' :
                        answers[question.id]?.value === 'not_assessed' ? 'Not Assessed' :
                        answers[question.id]?.value === 'not_applicable' ? 'Not Applicable' :
                        'Not Assessed'}
                    </span>
                  )}
                </div>
                <h3 className="text-base font-medium text-gray-900">{question.question}</h3>
                {/* Preview of findings if available */}
                {answers[question.id]?.findings && (
                  <div className="mt-2 text-sm text-gray-600 line-clamp-2">
                    <span className="font-medium text-gray-700">Findings:</span> {answers[question.id]?.findings}
                  </div>
                )}
              </div>
              <div className="ml-4">
                <button 
                  className="p-1.5 rounded-full hover:bg-blue-50 text-blue-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPanel(question);
                  }}
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Render panel using portal */}
      {renderPanel()}

      {/* Task Creation Modal */}
      <TaskCreateModal
        isOpen={isTaskCreateModalOpen}
        onClose={() => setIsTaskCreateModalOpen(false)}
        onTaskCreated={handleTaskCreated}
        initialValues={taskCreateInitialValues}
      />
    </div>
  );
};

export default ControlSection;
